import React, { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Users, UserCheck, Video, CheckCircle, AlertTriangle, RefreshCw, CloudDownload, ExternalLink, Ticket, Mail, Phone, Ban, ChevronDown } from "lucide-react";
import RegistrationsTable from "@/components/admin/RegistrationsTable";
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { format, subDays, parseISO, startOfDay } from "date-fns";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import RegistrationDetailDialog from "@/components/admin/RegistrationDetailDialog";
import RegistrationGoalProgress from "@/components/dashboard/RegistrationGoalProgress";
import RegistrationsTimelineChart from "@/components/dashboard/RegistrationsTimelineChart";
import { cn } from "@/lib/utils";
import { Registration } from "@/types/registration";

interface DailyData {
  date: string;
  verified: number;
  flagged: number;
}

interface CategoryData {
  name: string;
  value: number;
}

interface SyncResult {
  success: boolean;
  totalFromEventbrite: number;
  uniqueOrders: number;
  inserted: number;
  updated: number;
  skipped: number;
  ticketsSynced: number;
  flagged: number;
  errors?: string[];
  syncedAt: string;
}

interface EventRegistrationsProps {
  embedded?: boolean;
  selectedEventId?: string | null;
  eventGoal?: number;
}

const COLORS = ["hsl(var(--primary))", "hsl(var(--secondary))", "hsl(142 76% 36%)", "hsl(38 92% 50%)", "hsl(262 83% 58%)", "hsl(0 84% 60%)"];

const EventRegistrations: React.FC<EventRegistrationsProps> = ({ 
  embedded = false,
  selectedEventId: propEventId,
  eventGoal = 300,
}) => {
  const { organization, isLoading: orgLoading } = useOrganization();
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [eventbriteCount, setEventbriteCount] = useState<number | null>(null);
  const [selectedRegistration, setSelectedRegistration] = useState<Registration | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [excludedOpen, setExcludedOpen] = useState(false);

  const fetchSyncStatus = useCallback(async () => {
    if (!organization?.id) return;
    
    const [{ data: syncData }, { data: countData }] = await Promise.all([
      supabase
        .from("settings")
        .select("value")
        .eq("key", "eventbrite_last_sync")
        .maybeSingle(),
      supabase
        .from("settings")
        .select("value")
        .eq("key", "eventbrite_attendee_count")
        .maybeSingle(),
    ]);
    
    if (syncData?.value) setLastSync(syncData.value);
    if (countData?.value) setEventbriteCount(parseInt(countData.value, 10));
  }, [organization?.id]);

  const fetchRegistrations = useCallback(async () => {
    if (!organization?.id) return;
    
    let query = supabase
      .from("event_registrations")
      .select("*")
      .eq("organization_id", organization.id)
      .order("created_at", { ascending: false });

    // Filter by event if provided
    if (propEventId) {
      query = query.eq("event_id", propEventId);
    }

    const { data, error } = await query;

    if (!error && data) {
      setRegistrations(data as Registration[]);
    }
    setLoading(false);
  }, [organization?.id, propEventId]);

  useEffect(() => {
    if (!orgLoading && organization?.id) {
      // In embedded mode, wait for eventId to be provided
      if (embedded && !propEventId) {
        return;
      }
      fetchRegistrations();
      fetchSyncStatus();
    }
  }, [orgLoading, organization?.id, propEventId, fetchRegistrations, fetchSyncStatus, embedded]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke<SyncResult>("sync-eventbrite-attendees", {
        body: { organization_id: organization?.id },
      });

      if (error) {
        console.error("Sync error:", error);
        toast.error("Sync failed", { description: error.message });
        return;
      }

      if (data?.success) {
        toast.success("Eventbrite sync complete", {
          description: `${data.uniqueOrders} orders: Added ${data.inserted}, updated ${data.updated}. ${data.ticketsSynced} tickets synced.${data.flagged > 0 ? ` ${data.flagged} flagged for review.` : ""}`,
        });
        setEventbriteCount(data.totalFromEventbrite);
        setLastSync(data.syncedAt);
        fetchRegistrations();
      } else {
        toast.error("Sync failed", { description: "Unknown error occurred" });
      }
    } catch (err) {
      console.error("Sync error:", err);
      toast.error("Sync failed", { description: "Could not connect to sync service" });
    } finally {
      setSyncing(false);
    }
  };

  const handleViewDetails = (reg: Registration) => {
    setSelectedRegistration(reg);
    setDetailOpen(true);
  };

  // Separate active and excluded registrations
  const activeRegistrations = useMemo(() => 
    registrations.filter(r => !r.is_excluded), 
    [registrations]
  );
  const excludedRegistrations = useMemo(() => 
    registrations.filter(r => r.is_excluded), 
    [registrations]
  );

  // Calculate metrics (using activeRegistrations only)
  const totalRegistrations = activeRegistrations.length;
  const inPersonCount = activeRegistrations.filter(r => {
    const formFields = r.form_fields as { form_fields?: { is_in_person?: boolean } } | null;
    const isInPerson = formFields?.form_fields?.is_in_person;
    if (isInPerson !== undefined) return isInPerson;
    return r.registration_source === "in_person_form" || r.registration_source === "eventbrite";
  }).length;
  const inPersonPercent = totalRegistrations > 0 ? Math.round((inPersonCount / totalRegistrations) * 100) : 0;

  const eventbriteCount_local = activeRegistrations.filter(r => r.registration_source === "eventbrite" || r.eventbrite_status).length;
  const syncRate = totalRegistrations > 0 ? Math.round((eventbriteCount_local / totalRegistrations) * 100) : 0;

  // Total tickets (sum of ticket_count, excluding excluded)
  const totalTickets = activeRegistrations.reduce((sum, r) => sum + (r.ticket_count || 1), 0);

  // Excluded tickets count (for display)
  const excludedTickets = excludedRegistrations.reduce((sum, r) => sum + (r.ticket_count || 1), 0);

  // Flagged registrations (has conflict and not reviewed, from active only)
  const flaggedRegistrations = activeRegistrations.filter(r => 
    r.has_ticket_conflict && !r.reviewed_at
  );
  const needsReviewCount = flaggedRegistrations.length;

  // Calculate verified/flagged ticket counts (from active only)
  const { verifiedTickets, flaggedTickets } = useMemo(() => {
    const verified = activeRegistrations
      .filter(r => !r.has_ticket_conflict || r.reviewed_at)
      .reduce((sum, r) => sum + (r.ticket_count || 1), 0);
    
    const flagged = activeRegistrations
      .filter(r => r.has_ticket_conflict && !r.reviewed_at)
      .reduce((sum, r) => sum + (r.ticket_count || 1), 0);

    return { verifiedTickets: verified, flaggedTickets: flagged };
  }, [activeRegistrations]);

  // Daily registrations for line chart with verified/flagged split (from active only)
  const getDailyData = useCallback((): DailyData[] => {
    const days = 30;
    const data: DailyData[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = startOfDay(subDays(new Date(), i));
      const dateStr = format(date, "yyyy-MM-dd");
      
      const dayRegs = activeRegistrations.filter(r => {
        const regDate = format(parseISO(r.created_at), "yyyy-MM-dd");
        return regDate === dateStr;
      });

      const verified = dayRegs
        .filter(r => !r.has_ticket_conflict || r.reviewed_at)
        .reduce((sum, r) => sum + (r.ticket_count || 1), 0);

      const flagged = dayRegs
        .filter(r => r.has_ticket_conflict && !r.reviewed_at)
        .reduce((sum, r) => sum + (r.ticket_count || 1), 0);

      data.push({ date: format(date, "MMM dd"), verified, flagged });
    }
    return data;
  }, [activeRegistrations]);

  // Timeline data for the shared chart component
  const timelineData = useMemo(() => getDailyData(), [getDailyData]);

  // Interest categories for pie chart (from active only)
  const getInterestData = (): CategoryData[] => {
    const categories: Record<string, number> = {};
    activeRegistrations.forEach(r => {
      const cat = r.interest_category || "Uncategorized";
      categories[cat] = (categories[cat] || 0) + 1;
    });
    return Object.entries(categories).map(([name, value]) => ({ name, value }));
  };

  // Attendee types for bar chart (from active only)
  const getAttendeeTypeData = (): CategoryData[] => {
    const types: Record<string, number> = {};
    activeRegistrations.forEach(r => {
      const type = r.attendee_type || "Unknown";
      types[type] = (types[type] || 0) + 1;
    });
    return Object.entries(types)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-6" : "p-8 space-y-6"}>
      {!embedded && (
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold font-display">Event Registrations</h1>
            <p className="text-muted-foreground">Analytics and management for event attendees</p>
          </div>
        </div>
      )}

      {/* Eventbrite Sync Status Card */}
      <Card className="border-orange-500/30 bg-orange-500/5">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-orange-500/10">
                <CloudDownload className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <h3 className="font-semibold">Eventbrite Sync</h3>
                <div className="text-sm text-muted-foreground space-y-0.5">
                  {lastSync ? (
                    <p>Last synced: {format(parseISO(lastSync), "MMM d, yyyy 'at' h:mm a")} ({formatDistanceToNow(parseISO(lastSync), { addSuffix: true })})</p>
                  ) : (
                    <p>Never synced</p>
                  )}
                  <p className="text-xs">Auto-syncs daily at 4:00 AM UTC</p>
                  <div className="flex items-center gap-3 text-xs flex-wrap">
                    {eventbriteCount !== null && (
                      <span className="flex items-center gap-1">
                        <Ticket className="h-3 w-3" />
                        <strong>{eventbriteCount}</strong> tickets from Eventbrite
                      </span>
                    )}
                    <span>
                      <strong>{totalRegistrations}</strong> registrations ({totalTickets} tickets)
                    </span>
                    {eventbriteCount !== null && eventbriteCount_local < eventbriteCount && (
                      <Badge variant="outline" className="text-amber-600 dark:text-amber-400 border-amber-500/30">
                        {eventbriteCount - eventbriteCount_local} unsynced
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Button 
              onClick={handleSync} 
              disabled={syncing}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {syncing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Goal Progress + Key Metrics Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Goal Progress */}
        <RegistrationGoalProgress
          verifiedTickets={verifiedTickets}
          flaggedTickets={flaggedTickets}
          goal={eventGoal}
          eventId={propEventId || ""}
          editable={false}
        />

        {/* Key Metrics Grid */}
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex flex-col items-center gap-1">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="h-4 w-4 text-primary" />
                </div>
                <p className="text-xl font-bold">{totalRegistrations}</p>
                <p className="text-xs text-muted-foreground">Registrations</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex flex-col items-center gap-1">
                <div className="p-2 rounded-lg bg-violet-500/10">
                  <Ticket className="h-4 w-4 text-violet-500" />
                </div>
                <p className="text-xl font-bold">{totalTickets}</p>
                <p className="text-xs text-muted-foreground">Tickets</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex flex-col items-center gap-1">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <UserCheck className="h-4 w-4 text-emerald-500" />
                </div>
                <p className="text-xl font-bold">{inPersonPercent}%</p>
                <p className="text-xs text-muted-foreground">In-Person</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <div className="flex flex-col items-center gap-1">
                <div className="p-2 rounded-lg bg-sky-500/10">
                  <Video className="h-4 w-4 text-sky-500" />
                </div>
                <p className="text-xl font-bold">{100 - inPersonPercent}%</p>
                <p className="text-xs text-muted-foreground">Remote</p>
              </div>
            </CardContent>
          </Card>

          <Card className={needsReviewCount > 0 ? "border-amber-500/30 bg-amber-500/5" : ""}>
            <CardContent className="p-4 text-center">
              <div className="flex flex-col items-center gap-1">
                <div className={cn("p-2 rounded-lg", needsReviewCount > 0 ? "bg-amber-500/10" : "bg-muted")}>
                  <AlertTriangle className={cn("h-4 w-4", needsReviewCount > 0 ? "text-amber-500" : "text-muted-foreground")} />
                </div>
                <p className="text-xl font-bold">{needsReviewCount}</p>
                <p className="text-xs text-muted-foreground">Needs Review</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Registrations Timeline Chart - NEW */}
      <RegistrationsTimelineChart 
        data={timelineData}
        showUserSignups={false}
      />

      {/* All Registrations Table */}
      <RegistrationsTable
        registrations={activeRegistrations}
        onViewDetails={handleViewDetails}
      />

      {/* Flagged Registrations Section */}
      {flaggedRegistrations.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Potential Registration Issues ({flaggedRegistrations.length})
            </CardTitle>
            <CardDescription>
              Orders with conflicting ticket types that may need follow-up
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {flaggedRegistrations.map(reg => (
                <div 
                  key={reg.id} 
                  className="p-4 rounded-lg bg-background border border-amber-500/20 space-y-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">
                        {reg.first_name} {reg.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">{reg.email}</p>
                      {reg.phone && (
                        <p className="text-sm text-muted-foreground">{reg.phone}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="flex-shrink-0">
                      {reg.ticket_count} ticket{reg.ticket_count !== 1 ? "s" : ""}
                    </Badge>
                  </div>
                  
                  <div className="p-2 rounded bg-amber-500/10 text-sm">
                    <p className="text-amber-600 dark:text-amber-400">
                      {reg.conflict_reason}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {reg.email && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={`mailto:${reg.email}`}>
                          <Mail className="h-4 w-4 mr-1" />
                          Email
                        </a>
                      </Button>
                    )}
                    {reg.phone && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={`tel:${reg.phone}`}>
                          <Phone className="h-4 w-4 mr-1" />
                          Call
                        </a>
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleViewDetails(reg)}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Excluded Registrations Section - NEW */}
      {excludedRegistrations.length > 0 && (
        <Collapsible open={excludedOpen} onOpenChange={setExcludedOpen}>
          <Card className="border-gray-500/30 bg-gray-500/5">
            <CardHeader className="pb-3">
              <CollapsibleTrigger className="flex items-center justify-between w-full text-left">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Ban className="h-4 w-4 text-gray-500" />
                    Excluded Registrations ({excludedRegistrations.length})
                    <Badge variant="secondary" className="ml-2">
                      {excludedTickets} tickets
                    </Badge>
                  </CardTitle>
                  <CardDescription className="mt-1">
                    These registrations are not counted toward your goal
                  </CardDescription>
                </div>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform text-muted-foreground",
                  excludedOpen && "rotate-180"
                )} />
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {excludedRegistrations.map(reg => (
                    <div 
                      key={reg.id} 
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border cursor-pointer hover:bg-muted/80 transition-colors"
                      onClick={() => handleViewDetails(reg)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{reg.email || "Unknown"}</p>
                        <p className="text-xs text-muted-foreground">
                          {reg.excluded_reason || "No reason specified"} • {reg.ticket_count} ticket{reg.ticket_count !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(reg);
                        }}
                      >
                        View
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Interest Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Interest Categories</CardTitle>
            <CardDescription>Distribution of attendee interests</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={getInterestData()}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    labelLine={false}
                  >
                    {getInterestData().map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--card))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Attendee Types */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Attendee Types</CardTitle>
            <CardDescription>Breakdown by registration type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={getAttendeeTypeData()} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} className="text-muted-foreground" />
                  <YAxis 
                    type="category" 
                    dataKey="name" 
                    tick={{ fontSize: 11 }} 
                    width={150}
                    className="text-muted-foreground"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--card))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                  />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sync Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-500" />
            Sync Status
          </CardTitle>
          <CardDescription>
            Overview of Eventbrite synchronization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-muted/50 border">
                <p className="text-2xl font-bold">{syncRate}%</p>
                <p className="text-sm text-muted-foreground">From Eventbrite</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 border">
                <p className="text-2xl font-bold">{eventbriteCount_local}</p>
                <p className="text-sm text-muted-foreground">Synced Records</p>
              </div>
            </div>

            {needsReviewCount === 0 ? (
              <div className="text-center py-6">
                <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                <p className="font-medium">All registrations are in good order</p>
                <p className="text-sm text-muted-foreground">No conflicts detected</p>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">
                  {needsReviewCount} registration{needsReviewCount !== 1 ? "s" : ""} flagged for review
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Registration Detail Dialog */}
      <RegistrationDetailDialog
        registration={selectedRegistration}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onReviewComplete={fetchRegistrations}
      />
    </div>
  );
};

export default EventRegistrations;
